import React, { memo, useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Row, Col } from 'antd';

import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import MyModal from 'v2/components/Shared/MyModal';
import NoDangerouslyHTML from 'v2/components/Shared/NoDangerouslyHTML/index';
import { logError } from 'v2/helpers/rxjs-operators/logError';
import useGetFingerprint from 'v2/hooks/useGetFingerprint';
import useGetOriginalUser from 'v2/hooks/useGetOriginalUser';
import useGetUser from 'v2/hooks/useGetUser';
import type IProfile from 'v2/interfaces/profile';
import type { IModalFiscalForm } from 'v2/interfaces/terms';
import { profileFactory } from 'v2/services/profile';
import { termsFactory } from 'v2/services/terms';

import Address from './address';
import ModalFiscaSchema from './validation';
import { CheckboxFormField } from '../FormFields/CheckboxFormField';
import { TextFormField } from '../FormFields/TextFormField';
import ModalMinor from '../ModalMinor';

interface ModalFiscalProps {
  opened: boolean;
  canSkip?: boolean;
  profile: IProfile;
  subtitle?: string;
  onClose: () => void;
  onComplete: (profile?: IProfile) => void;
  onSkip?: () => void;
  returnDataOnComplete?: boolean;
}

const ModalFiscal = memo(
  ({
    opened,
    onClose,
    onComplete,
    onSkip,
    profile,
    subtitle,
    returnDataOnComplete,
    canSkip = true
  }: ModalFiscalProps) => {
    const user = useGetUser();
    const originalUser = useGetOriginalUser();
    const fingerprint = useGetFingerprint();
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [modalMinor, setModalMinor] = useState(false);
    const openModalMinor = useCallback(() => setModalMinor(true), []);
    const closeModalMinor = useCallback(() => setModalMinor(false), []);
    const { search } = useLocation();
    const isAccessByControlAccess = user?.id !== originalUser?.id;

    const [term] = useObservable(() => termsFactory.loadFiscalTerm().pipe(logError()), []);

    const [updateUserAction] = useCallbackObservable(
      (model: IModalFiscalForm) => {
        setLoading(true);
        return profileFactory.updateProfile({ ...model, fingerprint }).pipe(
          tap(res => {
            toast.success('Perfil alterado com sucesso.');
            setLoading(false);
            const searchParams = new URLSearchParams(search);

            if (returnDataOnComplete && res && res.data && res.data.profile) {
              onComplete(res.data.profile);

              if (searchParams.has('backTo')) {
                window.location.replace(searchParams.get('backTo'));
              }

              return;
            }

            onComplete();

            if (searchParams.has('backTo')) {
              window.location.replace(searchParams.get('backTo'));
            }
          }),
          catchError(err => {
            if (err == 'DOC_UNDER_AGE') {
              openModalMinor();
              return of(true);
            }

            if (err == 'ON_BLACKLIST') {
              setLoading(false);
              toast.error('Não é possível alterar o perfil. Entre em contato com o suporte.');
              return of(true);
            }

            setLoading(false);
            toast.error('Erro ao atualizar perfil. Tente novamente!');
            return of(true);
          })
        );
      },
      [fingerprint]
    );

    const handleOnClose = () => {
      if (canSkip) {
        onSkip?.();
        return onClose();
      }

      if (location.pathname === '/producer/dashboard') {
        history.push('/');
        setTimeout(() => {
          history.push('/producer/dashboard');
        }, 0);
        return;
      }

      history.push('/producer/dashboard');
    };

    const initialValues = {
      address_zip_code: profile?.address_zip_code || '',
      address_street: profile?.address_street || '',
      address_neighborhood: profile?.address_neighborhood || '',
      address_state: profile?.address_state || '',
      address_number: profile?.address_number?.toString() || '',
      address_complement: profile?.address_complement || '',
      address_city_name: profile?.address_city_name || '',
      document_number: profile?.document_number || '',
      type: profile?.type || '',
      address_country: 'Brasil',
      fiscal_responsibility: false,
      producer_affiliate_terms: false
    };

    function getModalContent(
      formikProps: FormikProps<IModalFiscalForm>,
      getError: (field: keyof IModalFiscalForm) => any
    ) {
      if (isAccessByControlAccess) {
        return (
          <div className='flex flex-col gap-4 [&>ul]:pl-5'>
            <p className='mb-0! text-sm'>
              Percebemos que o titular desta conta ainda precisa concluir um passo importante:{' '}
              <strong>preencher alguns dados obrigatórios e aceitar nossos termos.</strong>
            </p>

            <p className='mb-0! text-sm'>
              Por isso,{' '}
              <strong>
                algumas funcionalidades estão temporariamente indisponíveis para você e para outros usuários com acesso
                a esta conta.
              </strong>
            </p>

            <p className='mb-0! text-sm'>
              <strong>O que precisa ser feito pelo titular da conta:</strong>
            </p>

            <ul className='list-disc [&>li]:mb-2'>
              <li>
                <p className='mb-0! text-sm'>
                  Preencher os dados cadastrais obrigatórios (Razão Social, CPF/CNPJ, Endereço e Celular);
                </p>
              </li>
              <li>
                <p className='mb-0! text-sm'>Aceitar o Termo de Uso da Eduzz.</p>
              </li>
            </ul>

            <p className='mb-0! text-sm'>
              Assim que esse processo for concluído, tudo volta a funcionar normalmente para você e para todos os
              usuários vinculados a essa conta.
            </p>

            <p className='mb-0! text-sm'>
              Se quiser, você pode avisar o titular da conta para agilizar esse processo. Estamos aqui para garantir
              mais segurança e transparência nas suas operações.
            </p>
          </div>
        );
      }

      return (
        <Form>
          <Row gutter={[16, 16]} wrap>
            <Col span={24}>
              <p className='mb-0!'>Preencha os seguintes dados obrigatórios:</p>
            </Col>

            <Col xs={24} sm={12}>
              <Field
                label='CPF/CNPJ'
                name='document_number'
                id='document_number'
                mask='document'
                disabled={Boolean(formikProps.initialValues.document_number)}
                component={TextFormField}
              />
            </Col>

            <Address {...formikProps} getError={getError} />

            <Col span={24}>
              <div className='ml-1'>
                <Field
                  label='Aceito o termo de responsabilidade fiscal'
                  name='fiscal_responsibility'
                  id='fiscal_responsibility'
                  component={CheckboxFormField}
                />
              </div>
              <NoDangerouslyHTML
                className='mt-[0.3rem] w-full rounded-sm border border-[#ced4da] bg-[#f3f3f3] p-2 text-sm [&_p]:mb-0!'
                html={term?.hint ?? ''}
              />
            </Col>

            <Col span={24}>
              <div className='ml-1'>
                <Field
                  label={
                    <>
                      Aceito os{' '}
                      <a href='https://eduzz.com/termos/' target='_blank' rel='noopener noreferrer'>
                        termos de uso
                      </a>
                    </>
                  }
                  name='producer_affiliate_terms'
                  id='producer_affiliate_terms'
                  component={CheckboxFormField}
                />
              </div>
            </Col>

            <Col span={24}>
              <p className='mb-0!'>
                Essa etapa é importante para garantir segurança jurídica, transparência nas transações e conformidade
                com nossos parceiros financeiros.
              </p>
            </Col>
          </Row>
        </Form>
      );
    }

    if (!opened) {
      return null;
    }

    return (
      <>
        <ModalMinor opened={modalMinor} onClose={closeModalMinor} />

        <Formik initialValues={initialValues} onSubmit={updateUserAction} validationSchema={ModalFiscaSchema}>
          {(formikProps: FormikProps<IModalFiscalForm>) => {
            const getError = (field: keyof IModalFiscalForm): any => {
              if (formikProps.touched[field] && formikProps.errors[field]) return formikProps.errors[field];
              return null;
            };

            return (
              <MyModal
                opened={opened}
                submitLoading={loading}
                showCancelButton={canSkip}
                onSubmit={() => {
                  formikProps.validateForm().then(errors => {
                    if (Object.keys(errors).length > 0) {
                      const firstErrorKey = Object.keys(errors)[0];
                      const errorElement = document.getElementById(firstErrorKey);
                      errorElement?.focus();
                      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }

                    formikProps.submitForm();
                  });
                }}
                onClose={handleOnClose}
                showOk={!isAccessByControlAccess}
                buttonCancel={isAccessByControlAccess ? 'Fechar' : 'Cancelar'}
                title={isAccessByControlAccess ? 'Estamos quase lá!' : 'Complete seu perfil'}
                message={
                  (subtitle ?? isAccessByControlAccess)
                    ? null
                    : 'Você precisa completar seus dados e aceitar nossos termos!'
                }
              >
                {getModalContent(formikProps, getError)}
              </MyModal>
            );
          }}
        </Formik>
      </>
    );
  }
);

export default ModalFiscal;
